import { useMutation } from '@apollo/client';
import { Button, LegalBody, useFlashMessage } from '@indigo-ag/component-module';
import { AgFinancingInput, AgFinancingPhase2Status, AgFinancingPhases, OpportunityRegional } from '@indigo-ag/schema';
import { UserContext } from 'core/components/GateWay/GateWay';
import i18next from 'core/i18n';
import { PhaseRoutes } from 'features/brazil-grower/components/Timeline/Timeline';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { financingAgFinancing, upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CollateralUtil, Phase2Util, WithConditionalTooltip, text } from 'shared';
import { DecisionModal } from '../../../../shared/DecisionModal/DecisionModal';

export const ApprovePhase2: React.FunctionComponent = () => {
  /**
   * State
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  /**
   * Context
   */
  const { agFinance, orderInfo } = useContext(FinanceProcessContext);
  const { isCreditLead } = useContext(UserContext);

  /**
   * History
   */
  const history = useHistory();

  const { addFlashMessage } = useFlashMessage();

  const approvePhase2 = (decisionNotes: string) => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinance?.id,
      phase2: {
        status: AgFinancingPhase2Status.APPROVED,
        decisionNotes
      }
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      },
      refetchQueries: [{ query: financingAgFinancing, variables: { agFinancingId: agFinance?.id } }],
      awaitRefetchQueries: true
    })
      .then(() => {
        history.push(PhaseRoutes[AgFinancingPhases.PHASE_3]);
        addFlashMessage({
          content: '',
          title: i18next.t('phase2Approved'),
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  const hasLuchesiFinished =
    CollateralUtil.hasBeenSent(agFinance?.phase2?.collateral?.status) ||
    CollateralUtil.hasBeenApproved(agFinance?.phase2?.collateral?.status);
  const hasCommercialApproval = Phase2Util.getCommercialApproval(orderInfo);

  const cantApprove =
    agFinance?.opportunityRegional === OpportunityRegional.INDUSTRIAL_SALE_AND_ALLIANCE
      ? !hasCommercialApproval
      : !hasLuchesiFinished || !hasCommercialApproval;

  if (!isCreditLead && Phase2Util.isWaitingCMApproval(agFinance?.phase2?.status)) {
    return null;
  }

  if (Phase2Util.isApproved(agFinance?.phase2?.status)) {
    return null;
  }

  return (
    <>
      <DecisionModal
        title={`${i18next.t('approve')} ${i18next.t('phase')} 2`}
        onConfirmAction={approvePhase2}
        open={isDecisionOpen}
        closeModal={() => setIsDecisionOpen(false)}
        notesRequired
      />
      <WithConditionalTooltip
        visible={!hasLuchesiFinished || !hasCommercialApproval}
        title={
          <LegalBody>
            {text.firstLetterUppercase(
              i18next.t(!hasLuchesiFinished ? 'waitingCollateralAnalysisMessage' : 'waitingCommercialApprovalMessage')
            )}
          </LegalBody>
        }
        placement="top"
      >
        <Button variant="primary" size="small" onClick={() => setIsDecisionOpen(true)} disabled={cantApprove}>
          {i18next.t('approve')}
        </Button>
      </WithConditionalTooltip>
    </>
  );
};
